import { assert } from "@glideapps/ts-necessities";
import { definedMap, hasOwnProperty, withDefault } from "collection-utils";
import randomSample from "lodash/sample";
import type {
    ColumnPropertyDescriptor,
    ColumnTypePredicate,
    EnumPropertyCase,
    EnumPropertyDescriptor,
    EnumVisual,
    GetAllowedColumnsFunction,
    MultiCasePropertyDescriptor,
    NumberPropertyDescriptor,
    NumberPropertyStyle,
    PropertyDescriptor,
    PropertyTableGetter,
    SchemaInspector,
    StringPropertyDescriptor,
    StringPropertyDescriptorCase,
    Subcomponent,
    SwitchPropertyDescriptor,
    TablePropertyDescriptor,
    ZapPropertyDescriptor,
} from ".";
import { isMultiCasePropertyDescriptor, RequiredKind } from ".";
import type { BasePrimitiveValue } from "../common/components/computation-types";
import { ImageAspectRatio } from "../common/components/image-types";
import type { TableName } from "../common/database-strings";
import type {
    Description,
    InputOutputTables,
    LegacyPropertyDescription,
    PropertyDescription,
    PropertySource,
    TableGlideType,
    TableRefGlideType,
    TypeSchema,
} from "../common/description";
import { makeTableRef, PropertyKind } from "../common/description";
import { defined } from "../support";
import type { SuperPropertySection } from "./property-sections";
import { PropertySection } from "./property-sections";
import {
    getColumnProperty,
    getEnumProperty,
    getNumberProperty,
    getStringProperty,
    getSwitchProperty,
    getTableProperty,
    getZapProperty,
    makeColumnProperty,
    makeEnumProperty,
    makeNumberProperty,
    makeStringProperty,
    makeSwitchProperty,
    makeTableProperty,
} from "./property-utils";

class PropertyHandler<TKind extends PropertyKind> {
    constructor(private readonly _kind: TKind, protected readonly name: string, public readonly label: string) {}

    public get kind(): TKind {
        return this._kind;
    }

    public get property(): PropertySource {
        return { name: this.name };
    }
}

export enum ColumnPropertyFlag {
    // "optional" is ignored.  It's only here to do `flag ? "required" : "optional"`
    Optional,
    Required,
    Editable,
    Searchable,
    Legacy,
    EmptyByDefault,
    DefaultCaption,
    EditedInApp,
    AllowUserProfileColumns,
}

export class ColumnPropertyHandler extends PropertyHandler<PropertyKind.Column> implements ColumnPropertyDescriptor {
    constructor(
        name: string,
        label: string,
        private readonly _flags: ReadonlyArray<ColumnPropertyFlag>,
        public readonly getIndirectTable: PropertyTableGetter | undefined,
        public readonly preferredNames: ReadonlyArray<string> | undefined,
        public readonly getAllowedColumns: GetAllowedColumnsFunction,
        public readonly preferredType: ColumnTypePredicate,
        public readonly section: SuperPropertySection
    ) {
        super(PropertyKind.Column, name, label);
    }

    public get required(): boolean {
        return this._flags.indexOf(ColumnPropertyFlag.Required) >= 0;
    }

    public get editable(): boolean {
        return this._flags.indexOf(ColumnPropertyFlag.Editable) >= 0;
    }

    public get searchable(): boolean {
        return this._flags.indexOf(ColumnPropertyFlag.Searchable) >= 0;
    }

    public get isLegacy(): boolean {
        return this._flags.indexOf(ColumnPropertyFlag.Legacy) >= 0;
    }

    public get emptyByDefault(): boolean {
        return this._flags.indexOf(ColumnPropertyFlag.EmptyByDefault) >= 0;
    }

    public get isDefaultCaption(): boolean {
        return this._flags.indexOf(ColumnPropertyFlag.DefaultCaption) >= 0;
    }

    public get isEditedInApp(): boolean {
        return this._flags.indexOf(ColumnPropertyFlag.EditedInApp) >= 0;
    }

    public get allowUserProfileColumns(): boolean {
        return this._flags.indexOf(ColumnPropertyFlag.AllowUserProfileColumns) >= 0;
    }

    public getProperty(desc: Description): LegacyPropertyDescription | undefined {
        return (desc as any)[this.name];
    }

    public getColumnName(desc: Description): string | undefined {
        return getColumnProperty(this.getProperty(desc));
    }

    public setInDescription<TDesc>(desc: TDesc, columnName: string): TDesc {
        desc = { ...desc };
        (desc as any)[this.name] = makeColumnProperty(columnName);
        return desc;
    }
}

export class TablePropertyHandler extends PropertyHandler<PropertyKind.Table> implements TablePropertyDescriptor {
    constructor(
        name: string,
        label: string,
        public readonly required: boolean,
        public readonly getAllowedTables: (
            desc: Description,
            schema: TypeSchema,
            componentTables: InputOutputTables | undefined
        ) => ReadonlyArray<TableGlideType>,
        public readonly section: PropertySection
    ) {
        super(PropertyKind.Table, name, label);
    }

    public getTableName(desc: Description): TableName | undefined {
        return getTableProperty((desc as any)[this.name]);
    }

    public getTableRef(desc: Description): TableRefGlideType | undefined {
        return definedMap(this.getTableName(desc), makeTableRef);
    }

    public lookupTable(desc: Description, schema: SchemaInspector): TableGlideType | undefined {
        return definedMap(this.getTableRef(desc), ref => schema.findTable(ref));
    }

    public setInDescription<TDesc>(desc: TDesc, t: TableName): TDesc {
        desc = { ...desc };
        (desc as any)[this.name] = makeTableProperty(t);
        return desc;
    }
}

export class StringPropertyHandler extends PropertyHandler<PropertyKind.String> implements StringPropertyDescriptor {
    constructor(
        name: string,
        label: string,
        public readonly placeholder: string,
        public readonly required: boolean,
        public readonly isCaption: string | undefined,
        public readonly section: PropertySection
    ) {
        super(PropertyKind.String, name, label);
    }

    public getString(desc: Description): string | undefined {
        return getStringProperty((desc as any)[this.name]);
    }

    public setInDescription<TDesc>(desc: TDesc, str: string): TDesc {
        desc = { ...desc };
        (desc as any)[this.name] = makeStringProperty(str);
        return desc;
    }
}

export class NumberPropertyHandler<TObject extends Record<string, number>>
    extends PropertyHandler<PropertyKind.Number>
    implements NumberPropertyDescriptor
{
    public readonly defaultValue: number;
    constructor(
        defaultObject: TObject,
        label: string,
        public readonly placeholder: string,
        public readonly required: RequiredKind,
        public readonly style: NumberPropertyStyle,
        public readonly section: PropertySection,
        public readonly position?: number
    ) {
        super(PropertyKind.Number, defined(Object.keys(defaultObject)[0]), label);

        this.defaultValue = defaultObject[this.name];
    }

    public getNumber(desc: Description | undefined): number | undefined {
        const value = getNumberProperty((desc as any)?.[this.name]);
        if (value !== undefined) return value;
        if (this.required !== RequiredKind.NotRequiredDefaultMissing) return this.defaultValue;
        return undefined;
    }

    public setInDescription<T>(desc: T, n: number): T {
        desc = { ...desc };
        (desc as any)[this.name] = makeNumberProperty(n);
        return desc;
    }
}

export class SwitchPropertyHandler<TObject extends Record<string, boolean>>
    extends PropertyHandler<PropertyKind.Switch>
    implements SwitchPropertyDescriptor
{
    public readonly defaultValue: boolean;
    constructor(
        defaultObject: TObject,
        label: string,
        public readonly section: SuperPropertySection,
        public readonly subcomponent?: Subcomponent
    ) {
        super(PropertyKind.Switch, defined(Object.keys(defaultObject)[0]), label);

        this.defaultValue = defaultObject[this.name];
    }

    public getSwitch(desc: Description | undefined): boolean {
        if (desc === undefined) return this.defaultValue;
        return withDefault(getSwitchProperty((desc as any)[this.name]), this.defaultValue);
    }

    public getRandom(): boolean {
        return defined(randomSample([false, true]));
    }

    public getProperty(value: boolean): { [K in keyof TObject]: PropertyDescription } {
        return { [this.name]: makeSwitchProperty(value) } as {
            [K in keyof TObject]: PropertyDescription;
        };
    }

    public get default(): { [K in keyof TObject]: PropertyDescription } {
        return this.getProperty(this.defaultValue);
    }
}

export class EnumPropertyHandler<P extends BasePrimitiveValue, TObject extends Record<string, P>>
    extends PropertyHandler<PropertyKind.Enum>
    implements EnumPropertyDescriptor<P>
{
    public readonly defaultCaseValue: P;

    constructor(
        defaultObject: TObject,
        label: string,
        public readonly menuLabel: string,
        public readonly cases: readonly EnumPropertyCase<P>[],
        public readonly section: SuperPropertySection,
        public readonly visual: EnumVisual,
        public readonly getIndirectTable?: PropertyTableGetter,
        public readonly subcomponent?: Subcomponent,
        public readonly isSearchable?: boolean
    ) {
        super(PropertyKind.Enum, defined(Object.keys(defaultObject)[0]), label);

        this.defaultCaseValue = defaultObject[this.name];
    }

    public getEnum(desc: Description | undefined): P {
        if (desc === undefined) return this.defaultCaseValue;
        return withDefault(getEnumProperty((desc as any)[this.name]), this.defaultCaseValue);
    }

    public getRandom(): P {
        return defined(randomSample(this.cases)).value;
    }

    public getProperty(value: P): { [K in keyof TObject]: PropertyDescription } {
        return { [this.name]: makeEnumProperty(value) } as {
            [K in keyof TObject]: PropertyDescription;
        };
    }

    public get default(): { [K in keyof TObject]: PropertyDescription } {
        return this.getProperty(this.defaultCaseValue);
    }

    public setInDescription<TDesc>(desc: TDesc, value: P): TDesc {
        return { ...desc, [this.name]: makeEnumProperty(value) };
    }

    public withGetIndirectTable(getIndirectTable: PropertyTableGetter | undefined): EnumPropertyHandler<P, TObject> {
        assert(this.getIndirectTable === undefined);

        if (getIndirectTable === undefined) return this;

        const defaultObject = { [this.name]: this.defaultCaseValue } as TObject;
        return new EnumPropertyHandler(
            defaultObject,
            this.label,
            this.menuLabel,
            this.cases,
            this.section,
            this.visual,
            getIndirectTable
        );
    }
}

export class ZapPropertyHandler extends PropertyHandler<PropertyKind.Zap> implements ZapPropertyDescriptor {
    constructor(name: string, label: string, public readonly section: PropertySection) {
        super(PropertyKind.Zap, name, label);
    }

    public getZap(desc: Description): string | undefined {
        return getZapProperty((desc as any)[this.name]);
    }
}

export const isRequiredPropertyHandler = new SwitchPropertyHandler(
    { isRequired: false },
    "Required",
    PropertySection.Options
);

function getDefaultCaption(descr: PropertyDescriptor): string | undefined {
    if (!hasOwnProperty(descr.property, "name")) return undefined;

    if (isMultiCasePropertyDescriptor(descr)) {
        for (const c of descr.cases) {
            if (c.kind === PropertyKind.String) {
                return (c as StringPropertyDescriptorCase).isCaption;
            }
        }
    } else if (descr.kind === PropertyKind.String) {
        return descr.isCaption;
    } else {
        return undefined;
    }
    return undefined;
}

export type CaptionPropertyDescriptor = StringPropertyDescriptor | MultiCasePropertyDescriptor;

export function findCaptionDescriptor(
    properties: ReadonlyArray<PropertyDescriptor>
): CaptionPropertyDescriptor | undefined {
    return properties.find(d => getDefaultCaption(d) !== undefined) as CaptionPropertyDescriptor | undefined;
}

export function makeImageHeightPropertyHandler(
    defaultCaseValue: ImageAspectRatio,
    includeFit: boolean,
    includeCircle: boolean,
    includeExtendedOptions: boolean,
    label?: string
): EnumPropertyHandler<ImageAspectRatio, { size: ImageAspectRatio }> {
    const cases: EnumPropertyCase<ImageAspectRatio>[] = [
        {
            value: ImageAspectRatio.Square,
            label: "Square",
            icon: "borderSquare",
        },
        {
            value: ImageAspectRatio.ThreeByOne,
            label: "3:1",
            icon: "border3by1",
        },
        {
            value: ImageAspectRatio.ThreeByTwo,
            label: "3:2",
            icon: "border3by2",
        },
    ];
    if (includeCircle) {
        cases.push({ value: ImageAspectRatio.Circle, label: "Circle", icon: "borderCircle" });
    }

    if (includeExtendedOptions) {
        cases.push(
            {
                value: ImageAspectRatio.FourByThree,
                label: "4:3",
                icon: "border4by3",
            },
            {
                value: ImageAspectRatio.TwoByThree,
                label: "2:3 (Vertical)",
                icon: "border2by3",
            },
            {
                value: ImageAspectRatio.ThreeByFour,
                label: "3:4 (Vertical)",
                icon: "border3by4",
            }
        );
    }
    if (includeFit) {
        cases.push({
            value: ImageAspectRatio.Fit,
            label: "Original",
            icon: "borderOriginal",
        });
    }

    return new EnumPropertyHandler(
        { size: defaultCaseValue },
        withDefault(label, "Size"),
        "Image height",
        cases,
        PropertySection.Design,
        includeExtendedOptions ? "dropdown" : "small-images"
    );
}

export const visibilityPropertyDescriptor: PropertyDescriptor = {
    kind: PropertyKind.Transforms,
    property: { name: "visibilityFilters" },
    // FIXME: This label is not used
    label: "Show component",
    addText: "Add Condition",
    description: "Set conditions for when the component should be visible.",
    specPrefix: "Show component when",
    allowContextTable: true,
    allowLHSUserProfileColumns: true,
    getIndirectTable: tables => tables.output,
    section: PropertySection.Visibility,
    withContainingScreen: false,
};
